import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import VideoPlaceholder from '../placeholder';
import { getTimeString } from '../../../lib/utils';
import WarningIcon from '../assets/warning.svg?react';

const YouTubePlayerWrapper = styled.div`
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    padding-top: 25px;
    height: 0;

    & iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        display: ${(props) => (props.showThumb ? 'none' : 'block')};
    }
`;

export const ErrorContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-color: var(--gray200);
    border-radius: 4px;

    & svg {
        width: 18px;
        height: 18px;
        fill: var(--gray700);
        margin-bottom: 8px;
    }
`;

const Errormessage = styled.span`
    font-size: 14px;
    letter-spacing: 0.37px;
    line-height: 16px;
    color: var(--gray700);
`;

export const extractVideoId = (videoIdOrSource) => {
    if (videoIdOrSource.toLowerCase().startsWith('http')) {
        const urlParams = new URLSearchParams(videoIdOrSource.split('?')[1]);
        return urlParams.get('v');
    }
    return videoIdOrSource;
};

const videoOptions = {
    autoplay: 0,
    control: 1,
    cc_load_policy: 1,
    color: 'white',
    disablekb: 0,
    fs: 1,
    hl: 'it',
    loop: 0,
    playsinline: 1,
    rel: 0,
    showinfo: 1,
};

const YouTubePlayer = ({ videoIdOrSource, onPlayVideo = () => {}, showPreview = true, autoPlay = false }) => {
    const [title, setTitle] = useState();
    const [duration, setDuration] = useState();
    const [showThumb, setShowThumb] = useState(true);
    const [youtubePlayer, setYouTubePlayer] = useState();
    const [loaded, setLoaded] = useState(false);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        const id = extractVideoId(videoIdOrSource);

        const loadVideo = () => {
            // the Player object is created uniquely based on the id in props
            const player = new window.YT.Player(`youtube-player-${id}`, {
                videoId: id,
                host: 'https://www.youtube-nocookie.com',
                playerVars: videoOptions,
                events: {
                    onReady: onPlayerReady,
                    onError: () => setShowError(true),
                },
            });
            setYouTubePlayer(player);
        };

        if (!window.YT) {
            // If not, load the script asynchronously
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';

            // onYouTubeIframeAPIReady will load the video after the script is loaded
            window.onYouTubeIframeAPIReady = loadVideo;

            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } else {
            // If script is already there, load the video directly
            loadVideo();
        }
    }, [videoIdOrSource]);

    useEffect(() => {
        return () => {
            if (youtubePlayer) {
                youtubePlayer.destroy();
            }
        };
    }, [youtubePlayer]);

    useEffect(() => {
        setShowThumb(showPreview);
    }, [showPreview]);

    useEffect(() => {
        if (loaded && autoPlay) {
            setShowThumb(false);
            youtubePlayer.playVideo();
        }
    }, [loaded, autoPlay, youtubePlayer]);

    const onPlayerReady = (event) => {
        const duration = event.target.getDuration();

        if (duration === 0) {
            setShowError(true);
        } else {
            setTitle(event.target.getVideoData().title);
            setDuration(getTimeString(duration));
            setYouTubePlayer(event.target);
        }

        setLoaded(true);
    };

    const playVideo = (event) => {
        if (loaded) {
            event.stopPropagation();
            setShowThumb(false);
            youtubePlayer.playVideo();
            onPlayVideo();
        }
    };

    const videoId = extractVideoId(videoIdOrSource);

    return (
        <YouTubePlayerWrapper showThumb={showThumb}>
            {!showError && (
                <div>
                    <div id={`youtube-player-${videoId}`} />
                </div>
            )}

            {showError || !navigator.onLine ? (
                <ErrorContainer>
                    <WarningIcon />
                    <Errormessage>{"C'è un problema con il video."}</Errormessage>
                    <Errormessage>{'Ricarica la pagina o contattaci.'}</Errormessage>
                </ErrorContainer>
            ) : null}

            {showThumb && !showError && loaded && (
                <VideoPlaceholder
                    title={title}
                    duration={duration}
                    imageSource={`http://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    imageSourceFallback={`http://img.youtube.com/vi/${videoId}/0.jpg`}
                    onClick={(event) => playVideo(event)}
                    disabled={!loaded}
                />
            )}
        </YouTubePlayerWrapper>
    );
};

YouTubePlayer.propTypes = {
    videoIdOrSource: PropTypes.string,
    onPlayVideo: PropTypes.func,
    showPreview: PropTypes.bool,
    autoPlay: PropTypes.bool,
};

export default YouTubePlayer;
